// src/pages/Machines.js

import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Modal from 'react-modal';

// Import product images
import vf5Image from '../assets/vf5.png';
import vf4Image from '../assets/vf4.png';
import vf2Image from '../assets/vf2.png';
import hardinge from '../assets/hardinge.png';
import vm2 from '../assets/vm2.png';
import bridgeport from '../assets/bridgeport.png';

Modal.setAppElement('#root');

function Machine() {
  document.title = 'Machines';

  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [shops, setShops] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data: machinesData, error: machinesError } = await supabase
          .from('machines')
          .select('id, name, shop_id, current_status, capacity_per_day, current_job_id');
        if (machinesError) throw new Error(`Error fetching machines: ${machinesError.message}`);
        setMachines(machinesData || []);

        const { data: jobsData, error: jobsError } = await supabase
          .from('jobs')
          .select('id, po_number, po_line_number, part_number, status')
          .neq('status', 'closed')
          .order('po_number');
        if (jobsError) throw new Error(`Error fetching jobs: ${jobsError.message}`);
        setJobs(jobsData || []);

        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw new Error(`Error fetching shops: ${shopsError.message}`);
        const shopMap = shopsData.reduce((map, shop) => {
          map[shop.id] = shop.name || 'Unknown';
          return map;
        }, {});
        setShops(shopMap);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSetActiveJob = async (machineId, jobId) => {
    try {
      if (!jobId) {
        const { error: updateError } = await supabase
          .from('machines')
          .update({ current_job_id: null, current_status: 'Not in Use' })
          .eq('id', machineId);
        if (updateError) throw new Error(`Error clearing active job: ${updateError.message}`);

        setMachines(prev =>
          prev.map(machine =>
            machine.id === machineId
              ? { ...machine, current_job_id: null, current_status: 'Not in Use' }
              : machine
          )
        );

        toast.success('Active job removed successfully!');
        setIsModalOpen(false);
        return;
      }

      const { data: existingAssignment, error: checkError } = await supabase
        .from('machines')
        .select('id, name')
        .eq('current_job_id', jobId)
        .limit(1);
      if (checkError) throw new Error(`Error checking existing assignment: ${checkError.message}`);

      if (existingAssignment.length > 0) {
        const assignedMachine = existingAssignment[0];
        toast.error(`This job is already assigned to ${assignedMachine.name || 'another machine'}.`);
        return;
      }

      const { error: updateError } = await supabase
        .from('machines')
        .update({ current_job_id: jobId, current_status: 'Active' })
        .eq('id', machineId);
      if (updateError) throw new Error(`Error setting active job: ${updateError.message}`);

      setMachines(prev =>
        prev.map(machine =>
          machine.id === machineId
            ? { ...machine, current_job_id: jobId, current_status: 'Active' }
            : machine
        )
      );

      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select('id, po_number, po_line_number, part_number, status')
        .neq('status', 'closed')
        .order('po_number');
      if (jobsError) throw new Error(`Error re-fetching jobs: ${jobsError.message}`);
      setJobs(jobsData || []);

      toast.success('Active job set successfully!');
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error setting active job:', err);
      toast.error(`Error: ${err.message}`);
    }
  };

  const getMachineImage = (machineName) => {
    switch (machineName.toLowerCase()) {
      case 'haas vf5':
        return vf5Image;
      case 'haas vf4':
        return vf4Image;
      case 'haas vf2':
        return vf2Image;
      case 'hardinge':
        return hardinge;
      case 'haas vm2':
        return vm2;
      case 'bridgeport':
        return bridgeport;
      default:
        return null;
    }
  };

  const jobOptions = [
    { value: '', label: 'None' },
    ...jobs.map(job => ({
      value: job.id,
      label: `${job.po_number} - Line ${job.po_line_number} - ${job.part_number}`,
    })),
  ];

  const northShopMachines = machines.filter(machine => machine.shop_id === 1);
  const southShopMachines = machines.filter(machine => machine.shop_id === 2);

  const openModal = (machine) => {
    setSelectedMachine(machine);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMachine(null);
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#64748b' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 1px #64748b' : 'none',
      borderRadius: '2px',
      minHeight: '36px',
      '&:hover': {
        borderColor: '#94a3b8',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#334155' : state.isFocused ? '#f1f5f9' : 'white',
      color: state.isSelected ? 'white' : '#1e293b',
      fontSize: '14px',
      '&:hover': {
        backgroundColor: state.isSelected ? '#334155' : '#f1f5f9',
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '2px',
      marginTop: '2px',
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '14px',
      color: '#1e293b',
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '14px',
      color: '#94a3b8',
    }),
  };

  const MachineCard = ({ machine }) => {
    const activeJob = jobs.find(job => job.id === machine.current_job_id);
    const machineImage = getMachineImage(machine.name);
    const isActive = machine.current_status === 'Active';

    return (
      <div
        className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-slate-300 transition-colors cursor-pointer"
        onClick={() => openModal(machine)}
      >
        {/* Machine Image */}
        {machineImage && (
          <div className="relative h-32 bg-slate-100">
            <img
              src={machineImage}
              alt={machine.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
            <div className="absolute bottom-2 left-3">
              <h3 className="text-lg font-semibold text-white">{machine.name}</h3>
            </div>
            <div className="absolute top-2 right-2">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${
                isActive ? 'bg-green-500 text-white' : 'bg-slate-500 text-white'
              }`}>
                {isActive ? 'Active' : 'Idle'}
              </span>
            </div>
          </div>
        )}

        {/* Card Content */}
        <div className="p-3">
          {!machineImage && (
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800">{machine.name}</h3>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${
                isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
              }`}>
                {isActive ? 'Active' : 'Idle'}
              </span>
            </div>
          )}

          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Capacity/Day</span>
              <span className="text-slate-800 font-medium">{machine.capacity_per_day || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Active Part</span>
              <span className={`font-medium ${activeJob ? 'text-slate-800' : 'text-slate-400'}`}>
                {activeJob ? activeJob.part_number : 'None'}
              </span>
            </div>
            {activeJob && (
              <div className="flex justify-between">
                <span className="text-slate-500">PO</span>
                <span className="text-slate-800 font-medium">{activeJob.po_number}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-100">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-300 border-t-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="h-12 px-4 flex items-center">
          <h1 className="text-lg font-semibold text-slate-800">Machines</h1>
        </div>
      </div>

      <div className="p-4 max-w-7xl mx-auto">
        {/* North Shop Section */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">North Shop</h2>
            <span className="text-xs text-slate-500">({northShopMachines.length} machines)</span>
          </div>
          {northShopMachines.length === 0 ? (
            <p className="text-sm text-slate-500">No machines in North Shop.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {northShopMachines.map(machine => (
                <MachineCard key={machine.id} machine={machine} />
              ))}
            </div>
          )}
        </div>

        {/* South Shop Section */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">South Shop</h2>
            <span className="text-xs text-slate-500">({southShopMachines.length} machines)</span>
          </div>
          {southShopMachines.length === 0 ? (
            <p className="text-sm text-slate-500">No machines in South Shop.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {southShopMachines.map(machine => (
                <MachineCard key={machine.id} machine={machine} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal for Setting Active Job */}
      {selectedMachine && (
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          className="bg-white rounded-sm shadow-xl max-w-md w-full mx-auto outline-none"
          overlayClassName="fixed inset-0 bg-black/30 flex justify-center items-center p-4"
          style={{
            overlay: { zIndex: 1000 },
            content: { overflow: 'visible' },
          }}
        >
          {/* Modal Header with Image */}
          {getMachineImage(selectedMachine.name) && (
            <div className="relative h-40">
              <img
                src={getMachineImage(selectedMachine.name)}
                alt={selectedMachine.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <div className="absolute bottom-3 left-4">
                <h2 className="text-xl font-semibold text-white">{selectedMachine.name}</h2>
                <p className="text-sm text-slate-300">{shops[selectedMachine.shop_id] || 'Unknown Shop'}</p>
              </div>
              <button
                onClick={closeModal}
                className="absolute top-3 right-3 text-white/80 hover:text-white"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Modal Content */}
          <div className="p-4">
            {!getMachineImage(selectedMachine.name) && (
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">{selectedMachine.name}</h2>
                  <p className="text-sm text-slate-500">{shops[selectedMachine.shop_id] || 'Unknown Shop'}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">Status</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${
                  selectedMachine.current_status === 'Active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {selectedMachine.current_status === 'Active' ? 'Active' : 'Idle'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">Capacity/Day</span>
                <span className="text-sm font-medium text-slate-800">
                  {selectedMachine.capacity_per_day || '-'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">Current Job</span>
                <span className="text-sm font-medium text-slate-800">
                  {jobs.find(job => job.id === selectedMachine.current_job_id)
                    ? `${jobs.find(job => job.id === selectedMachine.current_job_id).po_number} - ${
                        jobs.find(job => job.id === selectedMachine.current_job_id).part_number
                      }`
                    : 'None'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Set Active Job</label>
              <Select
                options={jobOptions}
                value={jobOptions.find(option => option.value === (selectedMachine.current_job_id || ''))}
                onChange={(selectedOption) =>
                  handleSetActiveJob(selectedMachine.id, selectedOption ? selectedOption.value : '')
                }
                placeholder="Search for a job..."
                className="w-full"
                classNamePrefix="select"
                isClearable
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Machine;
